// Guia do dia (item 67, canvas 1.4a): na primeira vez que a aba Jogar abre no dia, quem está no Grátis vê a folha
// "Hoje você tem 2 partidas grátis", com as regras do limite, Chamar um amigo, Jogue sem limite e BORA JOGAR. Conta
// nova vê "Bem-vindo à mesa!". Sem partidas do dia e sem bônus, a página do limite abre no lugar (item 69). Assinante
// nunca vê. No celular em pé, folha presa embaixo; deitado, painel à direita com a lista de salas à vista; no
// computador, diálogo no centro.
import { useContext, useEffect, useRef, useState } from 'react';
import { BONUS_POR_INDICACAO, precoEmReais } from '@truco6/planos';
import { imagemDaCarta } from '@truco6/ui';
import { config } from './config.ts';
import { decidirGuia, marcarGuiaVisto, textosDoGuia, tirarContaNova } from './guia-do-dia.ts';
import { avisoDoLimiteVisto, toqueEmAssinar, toqueEmIndique, useLimite } from './limite.ts';
import { ir } from './rotas.ts';
import { PlanoDaCasca, abrirPlanosNoBronze } from './SeloGratis.tsx';

// As cartas do dia (verso da casa), uma por partida: a já jogada apagada, com o ✓ dourado; a que sobra acesa.
// destaque: a que sobra ganha a borda dourada (o cartão "Só resta 1"); sem ele, o brilho dourado atrás das cartas.
export function CartasDoDia({ total, jogadas, destaque = false, rotulo }: { total: number; jogadas: number; destaque?: boolean; rotulo: string }) {
  const verso = imagemDaCarta('verso');
  return (
    <div className={'cartas-do-dia' + (destaque ? ' destaque' : ' brilho')} role="img" aria-label={rotulo}>
      {Array.from({ length: total }, (_, i) => (
        <span key={i} className={'carta-do-dia' + (i < jogadas ? ' jogada' : ' acesa')} style={{ rotate: `${(i - (total - 1) / 2) * 16}deg` }}>
          <img src={verso} alt="" />
          {i < jogadas && <i className="check-do-dia" aria-hidden="true"><Check /></i>}
        </span>
      ))}
    </div>
  );
}

export function Check({ tamanho = 13 }: { tamanho?: number }) {
  return <svg width={tamanho} height={tamanho} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5L20 7" /></svg>;
}
export function IconePresente({ tamanho = 20 }: { tamanho?: number }) {
  return <svg width={tamanho} height={tamanho} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="8" width="18" height="13" rx="2" /><path d="M12 8v13M3 12h18M12 8c-2-4-6-4-6-1s4 1 6 1c2 0 6 2 6-1s-4-3-6 1z" /></svg>;
}
function IconeCoroa() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 8l4 4 5-7 5 7 4-4-2 11H5L3 8z" /></svg>;
}
function Seta() {
  return <svg className="seta-linha" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>;
}

function desligadoNosTestes(): boolean {
  if (!config.semGuiaDoDia) return false;
  try { return localStorage.getItem('t6:guia-do-dia-ligado') !== '1'; } catch { return true; }
}

// Montado na aba Jogar. conta: o id da conta, para a chave do "já viu hoje".
export function GuiaDoDia({ conta }: { conta: string }) {
  const plano = useContext(PlanoDaCasca), l = useLimite();
  const [aberto, setAberto] = useState(false);
  const [nova, setNova] = useState(false);
  const decidido = useRef(false);
  useEffect(() => {
    if (decidido.current || desligadoNosTestes()) return;
    const agora = Date.now(), d = decidirGuia(plano, l, conta, agora);
    if (d === null) return; // o plano e o limite ainda não chegaram
    decidido.current = true;
    if (d === 'nada') return;
    marcarGuiaVisto(conta, agora);
    // Sem partidas do dia e sem bônus: a página "Você já jogou suas 2 partidas grátis de hoje" no lugar da folha.
    if (d === 'limite') { ir({ tela: 'limite' }); return; }
    setNova(tirarContaNova());
    setAberto(true);
    avisoDoLimiteVisto('entrada');
  }, [plano, l, conta]);
  if (!aberto || !l || l.limite === null) return null;
  return <FolhaDoGuia limite={l.limite} usadas={l.usadas} {...textosDoGuia(l, nova)} aoFechar={() => setAberto(false)} />;
}

// A folha em si (a vitrine e os testes montam direto).
export function FolhaDoGuia({ limite, usadas, titulo, bonus, aoFechar }: { limite: number; usadas: number; titulo: string; bonus: number | null; aoFechar: () => void }) {
  const folha = useRef<HTMLElement>(null);
  const arrasto = useRef<{ y: number; dy: number } | null>(null);
  const [dy, setDy] = useState(0);
  useEffect(() => {
    folha.current?.querySelector<HTMLButtonElement>('.bora-jogar')?.focus();
    const tecla = (ev: KeyboardEvent) => { if (ev.key === 'Escape') aoFechar(); };
    addEventListener('keydown', tecla);
    return () => removeEventListener('keydown', tecla);
  }, []);
  // Arrastar para baixo fecha, como BORA JOGAR. O arrasto começa fora dos botões.
  const comecar = (ev: React.PointerEvent) => {
    if ((ev.target as HTMLElement).closest('button')) return;
    arrasto.current = { y: ev.clientY, dy: 0 };
    (ev.currentTarget as HTMLElement).setPointerCapture?.(ev.pointerId);
  };
  const mover = (ev: React.PointerEvent) => {
    const a = arrasto.current;
    if (!a) return;
    a.dy = Math.max(0, ev.clientY - a.y);
    setDy(a.dy);
  };
  const soltar = () => {
    const a = arrasto.current;
    arrasto.current = null;
    setDy(0);
    if (a && a.dy > 80) aoFechar();
  };
  const preco = precoEmReais('bronze').replace(' ', '\u00a0'); // "R$ 1,99" nunca quebra a linha
  return (
    <div className="folha-fundo guia-fundo" onClick={(ev) => { if (ev.target === ev.currentTarget) aoFechar(); }}>
      <section ref={folha} className="folha simples guia-do-dia" role="dialog" aria-modal="true" aria-labelledby="titulo-guia"
        style={dy ? { transform: `translateY(${dy}px)` } : undefined}
        onPointerDown={comecar} onPointerMove={mover} onPointerUp={soltar} onPointerCancel={soltar}>
        <span className="pegador" aria-hidden="true" />
        <div className="cab-guia">
          <CartasDoDia total={limite} jogadas={usadas} rotulo={`${limite - Math.min(usadas, limite)} de ${limite} partidas grátis disponíveis hoje`} />
          <div className="titulo-guia">
            <span className="rotulo-guia">SUAS PARTIDAS DE HOJE</span>
            <h2 id="titulo-guia">{titulo}</h2>
            {bonus !== null && <span className="chip-bonus">+{bonus} bônus guardado</span>}
          </div>
        </div>
        <ul className="regras-guia">
          <li><Check tamanho={18} />Valem em qualquer mesa: sala aberta, de amigo ou valendo Tentos.</li>
          <li><Check tamanho={18} />A partida conta quando começa. Revanche conta como nova.</li>
          <li><Check tamanho={18} />As {limite} voltam à meia-noite. Partida grátis não acumula.</li>
        </ul>
        <div className="linhas-guia">
          <button className="linha-guia" onClick={() => { toqueEmIndique('guia'); ir({ tela: 'indique' }); }}>
            <span className="ico-linha" aria-hidden="true"><IconePresente /></span>
            <span className="texto-linha"><b>Chame um amigo</b><small>+{BONUS_POR_INDICACAO} partidas para você por amigo que criar conta</small></span>
            <Seta />
          </button>
          <button className="linha-guia dourada" onClick={() => { toqueEmAssinar('guia'); abrirPlanosNoBronze(); }}>
            <span className="ico-linha" aria-hidden="true"><IconeCoroa /></span>
            <span className="texto-linha"><b>Jogue sem limite</b><small>Bronze por {preco} por mês, partidas ilimitadas</small></span>
            <Seta />
          </button>
        </div>
        <button className="btn1 largo bora-jogar" onClick={aoFechar}>BORA JOGAR</button>
      </section>
    </div>
  );
}
